/**
 * InsuranceUserView - Daily Operations View for Insurance Module
 *
 * Design based on approved mockup:
 * - Header with title, search, refresh
 * - 5 KPI cards in a row
 * - 3 tabs: Contrats, Véhicules Assurés, Sinistres
 * - Sinistres tab includes full claims management (merged from ClaimsView)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../../i18n/format.js';
import { getAuthHeaders } from '../../api/core.js';
import { businessDaysBetween } from '../../utils/businessDays';
import {
  Shield, FileText, Car, AlertTriangle, Plus, RefreshCw, Loader2,
  XCircle, Eye, Trash2, ChevronDown, ChevronRight,
  FolderOpen, Folder, Archive, Clock, CheckCircle, MapPin, Euro, User, Bell, Send, Wrench, AlertCircle
} from 'lucide-react';
import api from '../../api.js';
import { useVehicles, useActiveDrivers } from '../../hooks/useDataHooks.js';
import { useAccordionState } from '../../hooks/useAccordionState.js';
import { useToast } from '../common/Toast.jsx';
import VehicleAddTool from './modals/VehicleAddTool.jsx';
import ContractDetailModal from './modals/ContractDetailModal.jsx';
import ContractForm from './forms/ContractForm.jsx';
import ActiveContractsModal from './modals/ActiveContractsModal.jsx';
import ExpiringContractsModal from './modals/ExpiringContractsModal.jsx';
import InsuredVehiclesModal from './modals/InsuredVehiclesModal.jsx';
import UninsuredVehiclesModal from './modals/UninsuredVehiclesModal.jsx';
import PremiumDetailModal from './modals/PremiumDetailModal.jsx';
import VehicleInsuranceDetailModal from './modals/VehicleInsuranceDetailModal.jsx';
import ClaimsDeclarationWorkflow from '../claims/ClaimsDeclarationWorkflow.jsx';
import ClaimDetailModal from '../claims/ClaimDetailModal.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { CLOSED_CLAIM_STATUSES } from '../../hooks/useVehicleInsuranceData.js';
import { buildTrulyInsuredIds } from '../../utils/insuranceStatus.js';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';

// Montant de reference d'un sinistre (facture > expertise > estimation).
const claimRefAmount = (c) =>
  parseFloat(c.montant_facture_garage || c.montant_expertise || c.estimated_amount) || 0;

// Tab configuration — le libellé est résolu au rendu via t('insurance.tabs.<id>')
const TABS = [
  { id: 'contracts', icon: FileText },
  { id: 'vehicles', icon: Car },
  { id: 'claims', icon: AlertTriangle, showCount: true },
];

// Contract status styles
const CONTRACT_STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  actif: 'bg-green-100 text-green-700',
  renewal: 'bg-orange-100 text-orange-700',
  renouvellement: 'bg-orange-100 text-orange-700',
  draft: 'bg-gray-100 text-gray-600',
  brouillon: 'bg-gray-100 text-gray-600',
  expired: 'bg-red-100 text-red-700',
  expiré: 'bg-red-100 text-red-700',
  suspended: 'bg-yellow-100 text-yellow-700',
  suspendu: 'bg-yellow-100 text-yellow-700',
  terminated: 'bg-gray-200 text-gray-600',
  résilié: 'bg-gray-200 text-gray-600',
};

// Claims status configuration (Fleet Manager Workflow)
// Les libellés sont résolus au rendu via t('insurance.claimStatus.<code>').
const CLAIM_STATUS_CONFIG = {
  brouillon: { color: 'bg-gray-100 text-gray-800', icon: FileText, group: 'action' },
  declaration_prete: { color: 'bg-orange-100 text-orange-800', icon: Send, group: 'action' },
  declaration_envoyee: { color: 'bg-yellow-100 text-yellow-800', icon: Send, group: 'action' },
  en_instruction: { color: 'bg-blue-100 text-blue-800', icon: Clock, group: 'current' },
  expert_pending: { color: 'bg-purple-100 text-purple-800', icon: Clock, group: 'current' },
  expertise_realisee: { color: 'bg-violet-100 text-violet-800', icon: CheckCircle, group: 'current' },
  rapport_recu: { color: 'bg-indigo-100 text-indigo-800', icon: FileText, group: 'current' },
  prise_en_charge_demandee: { color: 'bg-cyan-100 text-cyan-800', icon: Clock, group: 'current' },
  en_reparation: { color: 'bg-cyan-100 text-cyan-800', icon: Wrench, group: 'current' },
  attente_facture_garage: { color: 'bg-amber-100 text-amber-800', icon: Clock, group: 'current' },
  vei: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle, group: 'current' },
  attente_indemnisation: { color: 'bg-purple-100 text-purple-800', icon: Clock, group: 'current' },
  cloture: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle, group: 'done' },
  // Legacy statuses
  draft: { color: 'bg-gray-100 text-gray-800', icon: FileText, group: 'action' },
  declared: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, group: 'action' },
  acknowledged: { color: 'bg-blue-100 text-blue-800', icon: Clock, group: 'current' },
  expert_done: { color: 'bg-indigo-100 text-indigo-800', icon: FileText, group: 'current' },
  repair_authorized: { color: 'bg-cyan-100 text-cyan-800', icon: Clock, group: 'current' },
  indemnified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, group: 'done' },
  closed: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle, group: 'done' },
  rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, group: 'done' }
};

// Le statut de contrat affiché est résolu par t('insurance.contractStatus.<code>') ;
// les codes legacy en français (actif, expiré…) ont leur propre clé.

// Status order within groups — `done` must stay in sync with
// CLOSED_CLAIM_STATUSES (useVehicleInsuranceData.js) so that counters
// ("N en cours" / "N terminés") across views agree.
const CLAIM_STATUS_ORDER = {
  action: ['brouillon', 'declaration_prete', 'declaration_envoyee', 'draft', 'declared'],
  current: ['en_instruction', 'expert_pending', 'expertise_realisee', 'rapport_recu', 'prise_en_charge_demandee', 'en_reparation', 'attente_facture_garage', 'vei', 'attente_indemnisation', 'acknowledged', 'expert_done', 'repair_authorized'],
  done: ['cloture', 'abandonne', 'vei_conserve', 'vei_cede', 'indemnified', 'closed', 'settled', 'denied', 'rejected']
};

// Les types de sinistre sont résolus par t('insurance.claimType.<code>').

// SLA Badge component
function SLABadge({ claim }) {
  const { t } = useTranslation();
  const [slaInfo, setSlaInfo] = useState(null);

  useEffect(() => {
    // Calculate SLA using French business days (jours ouvrés)
    const now = new Date();
    let info = null;

    const checkDeadline = (deadlineField, label) => {
      if (claim[deadlineField]) {
        const deadline = new Date(claim[deadlineField]);
        deadline.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (today > deadline) {
          return { type: 'critical', label: label || t('insurance.sla.late'), days: businessDaysBetween(deadline, today) };
        }
        const daysLeft = businessDaysBetween(today, deadline);
        if (daysLeft <= 1) {
          return { type: 'warning', label: t('insurance.sla.daysBefore', { n: daysLeft }), days: daysLeft };
        }
      }
      return null;
    };

    if ((claim.status === 'brouillon' || claim.status === 'draft') && claim.event_date) {
      const days = businessDaysBetween(new Date(claim.event_date), now);
      if (days >= 5) {
        info = { type: 'critical', label: t('insurance.sla.late'), days };
      } else if (days >= 3) {
        info = { type: 'warning', label: t('insurance.sla.daysAfter', { n: days }), days };
      }
    } else if (claim.status === 'declaration_prete' || claim.status === 'declaration_envoyee' || claim.status === 'declared') {
      info = checkDeadline('sla_ar_deadline', t('insurance.sla.ackPending'));
      if (!info && (claim.declaration_envoyee_at || claim.declared_at)) {
        const refDate = claim.declaration_envoyee_at || claim.declared_at;
        const days = businessDaysBetween(new Date(refDate), now);
        if (days >= 2) info = { type: 'critical', label: t('insurance.sla.ackPending'), days };
        else if (days >= 1) info = { type: 'warning', label: t('insurance.sla.daysAfter', { n: days }), days };
      }
    } else if (claim.status === 'en_instruction' || claim.status === 'acknowledged') {
      info = checkDeadline('sla_expert_contact_deadline', t('insurance.sla.expertPending'));
    } else if (claim.status === 'expertise_realisee') {
      info = checkDeadline('sla_rapport_deadline', t('insurance.sla.reportPending'));
    } else if (claim.status === 'prise_en_charge_demandee') {
      info = checkDeadline('sla_pec_deadline', t('insurance.sla.coveragePending'));
    } else if (claim.status === 'attente_indemnisation') {
      info = checkDeadline('sla_reglement_deadline', t('insurance.sla.settlementPending'));
      if (!info) {
        const refDate = claim.facture_transmise_at || claim.reparation_terminee_at || claim.documents_vei_transmis_at;
        if (refDate) {
          const days = businessDaysBetween(new Date(refDate), now);
          if (days >= 15) info = { type: 'critical', label: t('insurance.sla.followUp'), days };
          else if (days >= 10) info = { type: 'warning', label: t('insurance.sla.daysAfter', { n: days }), days };
        }
      }
    }

    setSlaInfo(info);
  }, [claim, t]);

  if (!slaInfo) return null;

  const colors = {
    critical: 'bg-red-500 text-white',
    warning: 'bg-orange-400 text-white'
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${colors[slaInfo.type]} flex items-center gap-1`}>
      <Bell className="w-3 h-3" />
      {slaInfo.label}
    </span>
  );
}

// Claim Card Component
function ClaimCard({ claim, onView, onDelete }) {
  const { t, fmt } = useFormat();
  const config = CLAIM_STATUS_CONFIG[claim.status] || CLAIM_STATUS_CONFIG.brouillon;
  const StatusIcon = config.icon;

  // Devise de l'organisation du sinistre - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const formatCurrency = (amount) => (amount ? money(amount, claim) : '-');

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Header: Date and Type + SLA Badge */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${config.color}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {t(`insurance.claimStatus.${claim.status}`, { defaultValue: t('insurance.claimStatus.brouillon') })}
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {t(`insurance.claimType.${claim.type}`, { defaultValue: claim.type })}
            </span>
            <SLABadge claim={claim} />
          </div>

          {/* Event date */}
          <div className="text-sm font-medium text-gray-900 mb-1">
            {claim.event_date ? fmt.date(claim.event_date, 'long') : t('insurance.claimCard.noDate')}
          </div>

          {/* Vehicle and driver info */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
            <span className="flex items-center gap-1">
              <Car className="w-4 h-4" />
              {claim.vehicle_plate || t('insurance.claimCard.vehicleFallback', { id: claim.vehicle_id })}
              {claim.vehicle_info && <span className="text-gray-400 ml-1">({claim.vehicle_info})</span>}
            </span>
            {(claim.driver_name || claim.driver_id) && (
              <span className="flex items-center gap-1 text-gray-500">
                <User className="w-4 h-4" />
                {claim.driver_name || t('insurance.claimCard.driverFallback', { id: claim.driver_id })}
              </span>
            )}
          </div>

          {/* Location */}
          {claim.location && (
            <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
              <MapPin className="w-4 h-4" />
              <span className="truncate max-w-xs" title={claim.location}>{claim.location}</span>
            </div>
          )}

          {/* Amounts */}
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-700">
              <Euro className="w-4 h-4" />
              {t(claim.montant_expertise ? 'insurance.claimCard.expertiseAmount' : 'insurance.claimCard.estimatedAmount', {
                amount: formatCurrency(claim.montant_facture_garage || claim.montant_expertise || claim.estimated_amount)
              })}
            </span>
            {claim.montant_indemnisation && (
              <span className="text-green-600 font-medium">
                {t('insurance.claimCard.indemnifiedAmount', { amount: formatCurrency(claim.montant_indemnisation) })}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => onView(claim)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title={t('insurance.claimCard.viewDetails')}
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDelete(claim.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={t('common.actions.delete')}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function InsuranceUserView({ onMessage, initialTab, autoSelectClaimId, autoSelectContractId, createForVehicleId }) {
  const { t, fmt } = useFormat();
  // Toast notifications (use hook directly, fallback to onMessage prop if provided)
  const toast = useToast();
  const showMessage = (msg) => {
    // Use toast hook (preferred) or onMessage prop (legacy)
    if (toast?.custom) {
      toast.custom({ type: msg.type, title: msg.title, message: msg.message, action: msg.action });
    } else if (onMessage) {
      onMessage(msg);
    }
  };

  // Global search integration
  const { searchQuery } = useSearch();
  // Devise : celle de l'org de la ligne ; totaux convertis dans la devise du perimetre.
  const { money, moneyScope, sumScope } = useOrgCurrency();

  // Data state
  const [contracts, setContracts] = useState([]);
  const [vehicleInsurances, setVehicleInsurances] = useState([]);
  const [coverageModels, setCoverageModels] = useState([]);
  const { data: vehicles = [], isLoading: vehiclesLoading } = useVehicles();
  const { data: allDrivers = [], isLoading: driversLoading } = useActiveDrivers();
  const [rawClaims, setRawClaims] = useState([]);
  const [intermediaries, setIntermediaries] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // UI state - use initialTab if provided
  const [activeTab, setActiveTab] = useState(initialTab || 'contracts');
  const [selectedContract, setSelectedContract] = useState(null);

  // Claims UI state
  const [showClaimWorkflow, setShowClaimWorkflow] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  // Contract creation form state
  const [showContractForm, setShowContractForm] = useState(false);

  // VehicleAddTool state (used by KPI uninsured modal flow)
  const [showAddTool, setShowAddTool] = useState(false);
  const [selectedContractForTool, setSelectedContractForTool] = useState(null);

  // KPI modal state
  const [activeKpiModal, setActiveKpiModal] = useState(null);

  // Vehicle insurance detail modal state
  const [selectedVehicleForDetail, setSelectedVehicleForDetail] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [intermediaryFilter, setIntermediaryFilter] = useState('all');

  // Accordion state for claims
  const { isExpanded, toggleGroup: toggleAccordion } = useAccordionState({ viewCode: 'insurance-claims' });
  const toggleGroup = (group) => toggleAccordion(`group:${group}`);
  const toggleStatus = (status) => toggleAccordion(`status:${status}`);

  const loading = dataLoading || vehiclesLoading || driversLoading;

  // Enrich claims with driver and vehicle info (derived from hooks + raw claims)
  const claims = useMemo(() => {
    if (!rawClaims.length) return [];
    const driverMap = new Map(allDrivers.map(d => [d.id, d]));
    const vehicleMap = new Map(vehicles.map(v => [v.id, v]));
    return rawClaims.map(claim => {
      const driver = driverMap.get(claim.driver_id);
      const vehicle = vehicleMap.get(claim.vehicle_id);
      return {
        ...claim,
        driver_name: driver ? `${driver.first_name} ${driver.last_name}` : null,
        vehicle_plate: vehicle?.license_plate,
        vehicle_info: vehicle ? `${vehicle.make} ${vehicle.model}` : null,
      };
    });
  }, [rawClaims, allDrivers, vehicles]);

  const loadData = async () => {
    setDataLoading(true);
    try {
      const [cRes, viRes, clRes, suppRes, cmRes] = await Promise.all([
        api.insurance.getAll(),
        api.vehicleInsurance.getAll(),
        api.claim.getAll(),
        api.supplier.getAll(),
        api.coverageModel.getAll().catch(() => ({ ok: false }))
      ]);

      if (cRes.ok) setContracts(cRes.data || []);
      if (viRes.ok) setVehicleInsurances(viRes.data || []);
      if (cmRes.ok) setCoverageModels(cmRes.data || []);
      // Brokers now live exclusively in the suppliers table
      const brokerSuppliers = (suppRes.ok ? suppRes.data || [] : [])
        .filter(s => ['broker', 'insurer', 'agent'].includes(s.category));
      setIntermediaries(brokerSuppliers);

      setRawClaims(clRes.ok ? clRes.data || [] : []);
    } catch (err) {
      console.error('Error loading insurance data:', err);
    } finally {
      setDataLoading(false);
    }
  };

  // Load all data
  useEffect(() => {
    loadData();
  }, []);

  // Auto-select claim if provided
  useEffect(() => {
    if (autoSelectClaimId && claims.length > 0 && !loading) {
      const claim = claims.find(c => c.id === parseInt(autoSelectClaimId, 10));
      if (claim) {
        setSelectedClaim(claim);
        setActiveTab('claims');
      }
    }
  }, [autoSelectClaimId, claims, loading]);

  // Auto-select contract if provided
  useEffect(() => {
    if (autoSelectContractId && contracts.length > 0 && !loading) {
      const contract = contracts.find(c => c.id === parseInt(autoSelectContractId, 10));
      if (contract) {
        setSelectedContract(contract);
        setActiveTab('contracts');
      }
    }
  }, [autoSelectContractId, contracts, loading]);

  // Auto-open claim creation for a specific vehicle (from Vehicles action menu)
  const createForVehicleProcessed = React.useRef(false);
  useEffect(() => {
    if (createForVehicleId && !createForVehicleProcessed.current) {
      createForVehicleProcessed.current = true;
      setActiveTab('claims');
      setShowClaimWorkflow(true);
    }
  }, [createForVehicleId]);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const activeContracts = contracts.filter(c => c.status === 'active');
    const today = new Date();

    const expiringContracts = activeContracts.filter(c => {
      if (!c.expiry_date) return false;
      const expiry = new Date(c.expiry_date);
      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 90;
    });

    // A VI counts as "truly insured" only if removed_at IS NULL AND the
    // parent contract has status === 'active'. A VI pointing to a terminated
    // contract is a data leak, not an insurance. (see utils/insuranceStatus.js)
    const vehicleIds = new Set(vehicles.map(v => v.id));
    const trulyInsuredIds = buildTrulyInsuredIds(vehicleInsurances, contracts);
    const insuredVehicleIds = new Set([...trulyInsuredIds].filter(id => vehicleIds.has(id)));
    const insuredCount = insuredVehicleIds.size;

    // Kept for UI bits that still want the raw "active VI on any contract"
    // list (e.g. premium sum below — a mispriced VI on a terminated contract
    // would otherwise silently disappear from the totals).
    const activeInsurances = vehicleInsurances.filter(vi => !vi.removed_at && vehicleIds.has(vi.vehicle_id));

    const activeVehicles = vehicles.filter(v => v.status === 'active');
    const uninsuredVehicles = activeVehicles.filter(v => !insuredVehicleIds.has(v.id));
    const uninsuredCount = uninsuredVehicles.length;

    // Number() coercion: PostgreSQL `numeric` columns serialize as STRING via
    // the pg driver ("888.00"), so a bare `sum + vi.premium_amount` is string
    // concatenation — you get "0888.00" instead of 888, and the KPI header
    // displays "0888,00 € prime totale".
    // Somme MULTI-DEVISES : chaque prime est convertie dans la devise du perimetre
    // avant addition (une prime polonaise est en PLN). Voir hooks/useOrgCurrency.js.
    const totalPremium = sumScope(activeInsurances, vi => vi.premium_amount);

    const activeClaims = claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status));

    // Map contract_id -> number of OPEN claims on that contract. claim.contract_id
    // is a direct FK to insurance_contract, so no cross-join through vehicle_insurance
    // is needed — a claim stays attached to the contract it was filed under, even
    // if the vehicle is later reassigned.
    const openClaimsByContract = new Map();
    for (const cl of activeClaims) {
      if (!cl.contract_id) continue;
      openClaimsByContract.set(cl.contract_id, (openClaimsByContract.get(cl.contract_id) || 0) + 1);
    }
    const contractsWithClaims = openClaimsByContract.size;

    return {
      activeContractsList: activeContracts,
      activeContracts: activeContracts.length,
      expiringContractsList: expiringContracts,
      expiringContracts: expiringContracts.length,
      insuredVehicleIds,
      insuredVehicles: insuredCount,
      uninsuredVehiclesList: uninsuredVehicles,
      uninsuredVehicles: uninsuredCount,
      totalPremium,
      activeClaims: activeClaims.length,
      openClaimsByContract,
      contractsWithClaims,
    };
  }, [contracts, vehicleInsurances, vehicles, claims, sumScope]);

  // Get functions
  const getIntermediary = useCallback((id) => intermediaries.find(i => i.id === id), [intermediaries]);
  const getVehicle = useCallback((id) => vehicles.find(v => v.id === id), [vehicles]);

  // Filter contracts
  const filteredContracts = useMemo(() => {
    let result = [...contracts];
    if (statusFilter === 'all') {
      result = result.filter(c => c.status !== 'terminated');
    } else {
      result = result.filter(c => c.status === statusFilter);
    }
    if (intermediaryFilter !== 'all') {
      result = result.filter(c => c.intermediary_id === parseInt(intermediaryFilter));
    }
    if (searchQuery && searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => {
        const inter = getIntermediary(c.intermediary_id);
        return (
          (c.contract_number || '').toLowerCase().includes(q) ||
          (c.insurer_name || '').toLowerCase().includes(q) ||
          (inter?.company_name || '').toLowerCase().includes(q) ||
          (c.status || '').toLowerCase().includes(q)
        );
      });
    }
    // Sort: contrats incriminés d'abord (plus d'open claims en haut), puis
    // les contrats qui expirent bientôt, puis le reste. L'utilisateur voit
    // en premier ce sur quoi il doit agir sans avoir à scanner la liste.
    const openMap = kpis?.openClaimsByContract || new Map();
    result = [...result].sort((a, b) => {
      const ca = openMap.get(a.id) || 0;
      const cb = openMap.get(b.id) || 0;
      if (cb !== ca) return cb - ca;
      const da = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
      const db = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
      return da - db;
    });
    return result;
  }, [contracts, statusFilter, intermediaryFilter, searchQuery, getIntermediary, kpis]);

  // Filter vehicle insurances
  const filteredVehicleInsurances = useMemo(() => {
    let result = vehicleInsurances.filter(vi => !vi.removed_at);
    if (searchQuery && searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      result = result.filter(vi => {
        const vehicle = getVehicle(vi.vehicle_id);
        return (
          (vehicle?.license_plate || '').toLowerCase().includes(q) ||
          (vehicle?.make || '').toLowerCase().includes(q) ||
          (vehicle?.model || '').toLowerCase().includes(q) ||
          (vi.policy_number || '').toLowerCase().includes(q)
        );
      });
    }
    return result;
  }, [vehicleInsurances, searchQuery, getVehicle]);

  // Claims list for grouping
  const filteredClaims = useMemo(() => {
    let result = [...claims];
    if (searchQuery && searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        (c.claim_number || '').toLowerCase().includes(q) ||
        (c.driver_name || '').toLowerCase().includes(q) ||
        (c.vehicle_plate || '').toLowerCase().includes(q) ||
        (c.vehicle_info || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        (c.location || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [claims, searchQuery]);

  // Group claims for accordion
  const groupedClaims = useMemo(() => {
    // Les libellés de groupe sont résolus au rendu via t('insurance.claimGroups.<key>').
    const groups = {
      action: { icon: AlertTriangle, color: 'red', statuses: {}, totalCount: 0, claims: [], urgentCount: 0 },
      current: { icon: FolderOpen, color: 'orange', statuses: {}, totalCount: 0, claims: [] },
      done: { icon: Archive, color: 'emerald', statuses: {}, totalCount: 0, claims: [] }
    };

    Object.entries(CLAIM_STATUS_CONFIG).forEach(([status, config]) => {
      if (groups[config.group]) {
        groups[config.group].statuses[status] = [];
      }
    });

    const now = new Date();

    filteredClaims.forEach(claim => {
      const config = CLAIM_STATUS_CONFIG[claim.status] || CLAIM_STATUS_CONFIG.brouillon;
      const group = config.group;

      if (groups[group] && groups[group].statuses[claim.status]) {
        groups[group].statuses[claim.status].push(claim);
        groups[group].totalCount++;
        groups[group].claims.push(claim);

        // Count urgent items (using French business days)
        if (group === 'action') {
          if ((claim.status === 'brouillon' || claim.status === 'draft') && claim.event_date) {
            const days = businessDaysBetween(new Date(claim.event_date), now);
            if (days >= 3) groups[group].urgentCount++;
          }
          if ((claim.status === 'declaration_envoyee' || claim.status === 'declared') && (claim.declaration_envoyee_at || claim.declared_at)) {
            const refDate = claim.declaration_envoyee_at || claim.declared_at;
            const days = businessDaysBetween(new Date(refDate), now);
            if (days >= 2) groups[group].urgentCount++;
          }
          // Overdue on intermediate statuses with stored deadlines
          const deadlineFields = {
            en_instruction: 'sla_expert_contact_deadline',
            expertise_realisee: 'sla_rapport_deadline',
            prise_en_charge_demandee: 'sla_pec_deadline',
            attente_indemnisation: 'sla_reglement_deadline',
          };
          const dlField = deadlineFields[claim.status];
          if (dlField && claim[dlField]) {
            const deadline = new Date(claim[dlField]);
            deadline.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (today > deadline) groups[group].urgentCount++;
          }
        }
      }
    });

    return groups;
  }, [filteredClaims]);

  // Days until expiry
  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  // Contract workflow action
  const handleContractWorkflowAction = async (contractId, action) => {
    try {
      const workflowActions = {
        suspend: api.insurance.workflow.suspend,
        reactivate: api.insurance.workflow.reactivate,
        terminate: api.insurance.workflow.terminate,
      };

      const workflowMethod = workflowActions[action];
      if (workflowMethod) {
        const res = await workflowMethod(contractId);
        if (res.ok) {
          setSelectedContract(null);
          loadData();
        } else {
          console.error('Workflow action failed:', res.error);
        }
      }
    } catch (err) {
      console.error('Workflow action failed:', err);
    }
  };

  // Claim workflow action (uses api module for proper auth headers)
  const handleClaimWorkflowAction = async (claimId, action, transitionData = {}) => {
    try {
      const result = await api.claim.workflowAction(claimId, action, transitionData);

      if (result.ok) {
        const isClosed = result.data?.status === 'cloture' || result.data?.status === 'closed';

        // Check if email was generated
        if (result.email?.generated) {
          showMessage({
            title: isClosed ? t('insurance.messages.claimClosedTitle') : t('insurance.messages.emailGeneratedTitle'),
            message: isClosed
              ? t('insurance.messages.claimClosedWithEmail', {
                  id: claimId,
                  template: result.email.templateCode,
                  recipient: result.email.recipient || t('insurance.messages.recipientFallback')
                })
              : t('insurance.messages.emailReady', {
                  template: result.email.templateCode,
                  recipient: result.email.recipient || t('insurance.messages.recipientFallback')
                }),
            type: isClosed ? 'success' : 'info',
            action: {
              label: t('insurance.messages.downloadEml'),
              onClick: async () => {
                try {
                  const res = await fetch(
                    `/api/v1/email_templates/download-eml?entity_type=claim&entity_id=${claimId}&template_code=${result.email.templateCode}`,
                    { headers: getAuthHeaders() }
                  );
                  if (!res.ok) throw new Error('Download failed');
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = result.email.fileName || `claim_${claimId}_${result.email.templateCode}.eml`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } catch (e) {
                  console.error('EML download failed:', e);
                }
              }
            }
          });
        } else {
          showMessage({
            title: isClosed ? t('insurance.messages.claimClosedTitle') : t('insurance.messages.successTitle'),
            message: isClosed
              ? t('insurance.messages.claimClosed', { id: claimId })
              : t('insurance.messages.actionSuccess', { action }),
            type: 'success'
          });
        }

        // Refresh data and return closure signal
        if (isClosed) {
          setSelectedClaim(null);
          loadData();
          return { closed: true };
        } else if (result.data) {
          setSelectedClaim(prev => ({ ...prev, ...result.data }));
        }
        loadData();
      } else {
        showMessage({
          title: t('insurance.messages.errorTitle'),
          message: result.error?.message || t('insurance.messages.actionFailed', { action }),
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Workflow action failed:', error);
      showMessage({
        title: t('insurance.messages.errorTitle'),
        message: t('insurance.messages.actionError', { message: error.message }),
        type: 'error'
      });
    }
  };

  // Delete claim
  const handleDeleteClaim = async (claimId) => {
    if (!window.confirm(t('insurance.messages.confirmDeleteClaim'))) return;

    try {
      await api.claim.delete(claimId);
      showMessage({
        title: t('insurance.messages.successTitle'),
        message: t('insurance.messages.claimDeleted'),
        type: 'success'
      });
      loadData();
    } catch (error) {
      showMessage({
        title: t('insurance.messages.errorTitle'),
        message: t('insurance.messages.claimDeleteFailed'),
        type: 'error'
      });
    }
  };


  const handleLaunchAddToolFromKpi = (contract) => {
    setActiveKpiModal(null);
    setSelectedContractForTool(contract);
    setShowAddTool(true);
  };

  const handleViewContractFromKpi = (contract) => {
    setActiveKpiModal(null);
    setSelectedContract(contract);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-tour="insurance-user-view">
      {/* Contract Creation Form Modal */}
      {showContractForm && (
        <ContractForm
          contract={null}
          onClose={() => setShowContractForm(false)}
          onSuccess={() => {
            loadData();
            setShowContractForm(false);
          }}
        />
      )}

      {/* Claim Declaration Workflow Modal */}
      <ClaimsDeclarationWorkflow
        isOpen={showClaimWorkflow}
        onClose={() => setShowClaimWorkflow(false)}
        defaultVehicleId={createForVehicleId}
        onSuccess={async (newClaimId) => {
          await loadData();
          if (newClaimId) {
            try {
              const res = await api.claim.getById(newClaimId);
              if (res.ok && res.data) {
                setSelectedClaim(res.data);
              }
            } catch (err) {
              console.warn('Could not auto-open new claim:', err);
            }
          }
        }}
        onMessage={onMessage}
      />

      {/* View Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              {t('insurance.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4 text-blue-500" />
                {t('insurance.header.activeContracts', { count: kpis.activeContracts })}
              </span>
              <span className="text-gray-400">|</span>
              <span className="flex items-center gap-1">
                <Car className="w-4 h-4 text-emerald-500" />
                {t('insurance.header.insuredVehicles', { count: kpis.insuredVehicles })}
              </span>
              {kpis.uninsuredVehicles > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <span className="text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {t('insurance.header.uninsuredVehicles', { count: kpis.uninsuredVehicles })}
                  </span>
                </>
              )}
              {kpis.expiringContracts > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <span className="text-orange-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {t('insurance.header.toRenew', { count: kpis.expiringContracts })}
                  </span>
                </>
              )}
              {kpis.activeClaims > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <button
                    type="button"
                    onClick={() => setActiveTab('claims')}
                    className="text-amber-700 hover:text-amber-900 flex items-center gap-1 underline-offset-2 hover:underline"
                    title={t('insurance.header.openClaimsTitle')}
                  >
                    <AlertCircle className="w-4 h-4" />
                    {t('insurance.header.openClaims', { count: kpis.activeClaims })}
                    {kpis.contractsWithClaims > 0 && (
                      <span className="text-xs text-amber-600">
                        {t('insurance.header.claimedContracts', { count: kpis.contractsWithClaims })}
                      </span>
                    )}
                  </button>
                </>
              )}
              {kpis.totalPremium.value > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <span>{t('insurance.header.totalPremium', { amount: kpis.totalPremium.label })}</span>
                </>
              )}
            </p>
          </div>

          {/* Main Tab Toggle — Pill buttons (moved into header) */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const count = tab.id === 'claims' ? kpis.activeClaims : null;
              const isActive = activeTab === tab.id;
              const activeColors = {
                contracts: 'bg-blue-500 text-white shadow-sm',
                vehicles: 'bg-emerald-500 text-white shadow-sm',
                claims: 'bg-orange-500 text-white shadow-sm',
              };
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setStatusFilter('all');
                  }}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors text-sm ${
                    isActive
                      ? activeColors[tab.id]
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(`insurance.tabs.${tab.id}`)}
                  {tab.showCount && count !== null && count > 0 && (
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                      isActive
                        ? 'bg-white/25 text-white'
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              title={t('common.actions.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">

        {/* Filter Bar (for contracts/vehicles) */}
        {activeTab !== 'claims' && (
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            {activeTab === 'contracts' && (
              <button
                onClick={() => setShowContractForm(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                {t('insurance.actions.newContract')}
              </button>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('insurance.filters.allStatuses')}</option>
              {activeTab === 'contracts' && (
                <>
                  <option value="active">{t('insurance.contractStatus.active')}</option>
                  <option value="draft">{t('insurance.contractStatus.draft')}</option>
                  <option value="quote_requested">{t('insurance.contractStatus.quote_requested')}</option>
                  <option value="quote_received">{t('insurance.contractStatus.quote_received')}</option>
                  <option value="expired">{t('insurance.contractStatus.expired')}</option>
                  <option value="suspended">{t('insurance.contractStatus.suspended')}</option>
                  <option value="terminated">{t('insurance.contractStatus.terminated')}</option>
                </>
              )}
            </select>

            {activeTab === 'contracts' && (
              <select
                value={intermediaryFilter}
                onChange={(e) => setIntermediaryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('insurance.filters.allIntermediaries')}</option>
                {intermediaries.map(int => (
                  <option key={int.id} value={int.id}>{int.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Tab Content */}
        <div className={activeTab === 'claims' ? '' : 'p-6'}>
          {/* Contracts Tab */}
          {activeTab === 'contracts' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="pb-3">{t('insurance.contractsTable.contract')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.insurer')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.intermediary')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.vehicles')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.claims')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.expiry')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.premium')}</th>
                    <th className="pb-3">{t('insurance.contractsTable.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredContracts.length > 0 ? (
                    filteredContracts.map((contract) => {
                      const existingVehicleIds = new Set(vehicles.map(v => v.id));
                      const contractVIs = vehicleInsurances.filter(vi => vi.contract_id === contract.id && !vi.removed_at && existingVehicleIds.has(vi.vehicle_id));
                      const vehicleCount = contractVIs.length;
                      const contractPremium = contractVIs
                        .reduce((sum, vi) => sum + (Number(vi.premium_amount) || 0), 0);
                      const daysUntil = getDaysUntilExpiry(contract.expiry_date);
                      const isExpiringSoon = daysUntil !== null && daysUntil <= 30 && daysUntil >= 0;
                      const isExpired = daysUntil !== null && daysUntil < 0;
                      const intermediary = getIntermediary(contract.intermediary_id);
                      const openClaims = kpis.openClaimsByContract.get(contract.id) || 0;
                      const hasClaims = openClaims > 0;

                      let displayStatus = contract.status;
                      if (isExpiringSoon && contract.status === 'active') {
                        displayStatus = 'renouvellement';
                      }

                      return (
                        <tr
                          key={contract.id}
                          onClick={() => setSelectedContract(contract)}
                          className={`cursor-pointer transition-colors ${
                            hasClaims ? 'bg-amber-50/60 hover:bg-amber-100' : 'hover:bg-blue-50'
                          }`}
                        >
                          <td className="py-4">
                            <p className="font-medium text-gray-900">{contract.policy_number}</p>
                            <p className="text-xs text-gray-500">{contract.product_name || t(`insurance.contractType.${contract.contract_type}`, { defaultValue: t('insurance.contractsTable.fallbackProduct') })}</p>
                          </td>
                          <td className="py-4">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 text-xs font-bold bg-blue-600 text-white rounded">
                                {(contract.insurer_name || t('common.states.notAvailable')).substring(0, 3).toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-900">{contract.insurer_name || t('common.states.notAvailable')}</span>
                            </div>
                          </td>
                          <td className="py-4">
                            {intermediary ? (
                              <div>
                                <p className="text-sm text-gray-900">{intermediary.name}</p>
                                <p className="text-xs text-gray-500">{intermediary.type || t('insurance.contractsTable.broker')}</p>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-4">
                            <span className="text-blue-600 font-medium">
                              {t('insurance.contractsTable.vehicleCount', { count: vehicleCount })}
                            </span>
                          </td>
                          <td className="py-4">
                            {hasClaims ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab('claims');
                                }}
                                title={t('insurance.contractsTable.openClaimsTooltip', { count: openClaims })}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold transition-all ${
                                  openClaims >= 2
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                }`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                {openClaims}
                              </button>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="py-4">
                            <p className={`text-sm font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : 'text-gray-900'}`}>
                              {contract.expiry_date ? fmt.date(contract.expiry_date) : '—'}
                            </p>
                            {daysUntil !== null && (
                              <p className={`text-xs ${isExpired ? 'text-red-500' : isExpiringSoon ? 'text-orange-500' : 'text-gray-500'}`}>
                                {isExpired ? t('insurance.contractsTable.expired') : t('insurance.contractsTable.inDays', { n: daysUntil })}
                              </p>
                            )}
                          </td>
                          <td className="py-4">
                            <span className="font-medium text-gray-900">
                              {money(contractPremium, contract)}
                            </span>
                          </td>
                          <td className="py-4">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${CONTRACT_STATUS_STYLES[displayStatus] || 'bg-gray-100 text-gray-600'}`}>
                              {t(`insurance.contractStatus.${displayStatus}`, { defaultValue: displayStatus })}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="py-12 text-center text-gray-500">
                        {t('insurance.contractsTable.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Vehicles Tab */}
          {activeTab === 'vehicles' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="pb-3">{t('insurance.vehiclesTable.vehicle')}</th>
                    <th className="pb-3">{t('insurance.vehiclesTable.contract')}</th>
                    <th className="pb-3">{t('insurance.vehiclesTable.coverage')}</th>
                    <th className="pb-3">{t('insurance.vehiclesTable.effectiveDate')}</th>
                    <th className="pb-3">{t('insurance.vehiclesTable.premium')}</th>
                    <th className="pb-3">{t('insurance.vehiclesTable.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredVehicleInsurances.length > 0 ? (
                    filteredVehicleInsurances.map((vi) => {
                      const vehicle = getVehicle(vi.vehicle_id);
                      const contract = contracts.find(c => c.id === vi.contract_id);
                      // Coverage label — resolved from coverage_model_id (source
                      // unique). Legacy string column no longer read. French UI.
                      const model = vi.coverage_model_id
                        ? coverageModels.find(m => m.id === vi.coverage_model_id)
                        : null;
                      const level = model?.level;
                      const coverageLabel =
                        level === 'tous_risques' ? t('insurance.coverageLevel.tous_risques') :
                        level === 'tiers_plus' ? t('insurance.coverageLevel.tiers_plus') :
                        level === 'tiers_simple' ? t('insurance.coverageLevel.tiers_simple') :
                        level === 'economique' ? t('insurance.coverageLevel.economique') :
                        (model?.name || level || t('insurance.coverageLevel.standard'));

                      return (
                        <tr
                          key={vi.id}
                          onClick={() => vehicle && setSelectedVehicleForDetail(vehicle)}
                          className="hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          <td className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gray-100 rounded-lg">
                                <Car className="w-5 h-5 text-gray-500" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{vehicle?.license_plate || t('common.states.notAvailable')}</p>
                                <p className="text-xs text-gray-500">{vehicle?.make} {vehicle?.model}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <p className="text-sm text-gray-900">{contract?.policy_number || t('common.states.notAvailable')}</p>
                            <p className="text-xs text-gray-500">{contract?.insurer_name}</p>
                          </td>
                          <td className="py-4">
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                              {coverageLabel}
                            </span>
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            {vi.assigned_at ? fmt.date(vi.assigned_at) : '—'}
                          </td>
                          <td className="py-4">
                            <span className="font-medium text-gray-900">
                              {vi.premium_amount ? money(vi.premium_amount, vi) : '—'}
                            </span>
                          </td>
                          <td className="py-4">
                            <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                              {t('insurance.vehiclesTable.active')}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-12 text-center text-gray-500">
                        {t('insurance.vehiclesTable.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Claims Tab - Full accordion view */}
          {activeTab === 'claims' && selectedClaim && (
            <div className="p-4">
              <ClaimDetailModal
                claim={selectedClaim}
                onClose={() => setSelectedClaim(null)}
                onUpdate={loadData}
                onMessage={onMessage}
                onWorkflowAction={handleClaimWorkflowAction}
                inline
              />
            </div>
          )}

          {activeTab === 'claims' && !selectedClaim && (
            <div className="p-4 space-y-4">
              {/* Claims Header Bar */}
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1 text-red-600 font-medium">
                    <AlertCircle className="w-4 h-4" />
                    {t('insurance.claimsBar.toHandle', { n: groupedClaims.action.totalCount })}
                  </span>
                  {groupedClaims.action.urgentCount > 0 && (
                    <span className="flex items-center gap-1 text-red-500 font-medium">
                      <Bell className="w-4 h-4" />
                      {t('insurance.claimsBar.urgent', { count: groupedClaims.action.urgentCount })}
                    </span>
                  )}
                  <span className="text-gray-400">|</span>
                  <span className="flex items-center gap-1 text-orange-600">
                    <Clock className="w-4 h-4" />
                    {t('insurance.claimsBar.inProgress', { n: groupedClaims.current.totalCount })}
                  </span>
                  <span className="text-gray-400">|</span>
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle className="w-4 h-4" />
                    {t('insurance.claimsBar.done', { n: groupedClaims.done.totalCount })}
                  </span>
                  <span className="text-gray-400">|</span>
                  <span className="flex items-center gap-1">
                    <Euro className="w-4 h-4" />
                    {sumScope([...groupedClaims.action.claims, ...groupedClaims.current.claims], claimRefAmount).label}
                  </span>
                </div>
                <button
                  onClick={() => setShowClaimWorkflow(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  {t('insurance.actions.declareClaim')}
                </button>
              </div>

              {/* Empty state */}
              {claims.length === 0 && (
                <div className="text-center py-12">
                  <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('insurance.claimsEmpty.title')}</h3>
                  <p className="text-gray-600 mb-4">{t('insurance.claimsEmpty.description')}</p>
                  <button
                    onClick={() => setShowClaimWorkflow(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <Plus className="w-4 h-4" />
                    {t('insurance.actions.declareClaimShort')}
                  </button>
                </div>
              )}

              {/* Claims Accordion Groups */}
              {claims.length > 0 && (
                <div className="space-y-4">
                  {['action', 'current', 'done'].map(groupKey => {
                    const group = groupedClaims[groupKey];
                    if (group.totalCount === 0) return null;

                    const colorClasses = {
                      action: { bg: 'bg-red-50', hover: 'hover:bg-red-100', text: 'text-red-600', badge: 'bg-red-200 text-red-800', textDark: 'text-red-900', textLight: 'text-red-700' },
                      current: { bg: 'bg-orange-50', hover: 'hover:bg-orange-100', text: 'text-orange-600', badge: 'bg-orange-200 text-orange-800', textDark: 'text-orange-900', textLight: 'text-orange-700' },
                      done: { bg: 'bg-emerald-50', hover: 'hover:bg-emerald-100', text: 'text-emerald-600', badge: 'bg-emerald-200 text-emerald-800', textDark: 'text-emerald-900', textLight: 'text-emerald-700' }
                    };
                    const colors = colorClasses[groupKey];
                    const GroupIcon = group.icon;

                    return (
                      <div key={groupKey} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Group header */}
                        <button
                          onClick={() => toggleGroup(groupKey)}
                          className={`w-full px-6 py-4 flex items-center justify-between ${colors.bg} ${colors.hover} transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded(`group:${groupKey}`) ? (
                              <ChevronDown className={`w-5 h-5 ${colors.text}`} />
                            ) : (
                              <ChevronRight className={`w-5 h-5 ${colors.text}`} />
                            )}
                            <GroupIcon className={`w-5 h-5 ${colors.text}`} />
                            <span className={`font-semibold ${colors.textDark}`}>{t(`insurance.claimGroups.${groupKey}`)}</span>
                            <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${colors.badge}`}>
                              {group.totalCount}
                            </span>
                            {groupKey === 'action' && group.urgentCount > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500 text-white flex items-center gap-1">
                                <Bell className="w-3 h-3" />
                                {t('insurance.claimsBar.urgent', { count: group.urgentCount })}
                              </span>
                            )}
                          </div>
                          <div className={`text-sm ${colors.textLight}`}>
                            {t(groupKey === 'done' ? 'insurance.claimGroup.handled' : 'insurance.claimGroup.estimated', {
                              amount: sumScope(group.claims, claimRefAmount).label
                            })}
                          </div>
                        </button>

                        {/* Group content */}
                        {isExpanded(`group:${groupKey}`) && (
                          <div className="p-4 space-y-3">
                            {CLAIM_STATUS_ORDER[groupKey].map(status => {
                              const statusClaims = group.statuses[status] || [];
                              if (statusClaims.length === 0) return null;

                              const config = CLAIM_STATUS_CONFIG[status];
                              if (!config) return null;
                              const StatusIcon = config.icon;
                              const isStatusExpanded = isExpanded(`status:${status}`);

                              return (
                                <div key={status} className="border border-gray-200 rounded-lg overflow-hidden">
                                  {/* Status header */}
                                  <button
                                    onClick={() => toggleStatus(status)}
                                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      {isStatusExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                      )}
                                      <Folder className={`w-4 h-4 ${isStatusExpanded ? 'text-gray-500' : 'text-gray-400'}`} />
                                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${config.color}`}>
                                        <StatusIcon className="w-3 h-3 inline mr-1" />
                                        {t(`insurance.claimStatus.${status}`, { defaultValue: status })}
                                      </span>
                                      <span className="text-sm text-gray-600">
                                        {t('insurance.claimGroup.claimCount', { count: statusClaims.length })}
                                      </span>
                                    </div>
                                    {(() => {
                                      // Sous-totaux convertis dans la devise du perimetre.
                                      const totalRef = sumScope(statusClaims, claimRefAmount);
                                      const totalIndemnity = sumScope(statusClaims, c => c.indemnity_amount);
                                      const totalFranchise = sumScope(statusClaims, c => c.franchise_amount);
                                      const totalTva = Math.round((totalRef.value - totalRef.value / 1.20) * 100) / 100;
                                      const resteACharge = Math.round((totalFranchise.value + totalTva) * 100) / 100;
                                      const hasClosed = statusClaims.some(c => ['cloture', 'indemnified', 'settled'].includes(c.status));
                                      return hasClosed && totalIndemnity.value > 0 ? (
                                        <div className="text-right text-xs space-y-0.5">
                                          <span className="text-sm text-gray-600 font-medium">{totalRef.label}</span>
                                          <div className="text-green-600">{t('insurance.claimGroup.indemnity', { amount: totalIndemnity.label })}</div>
                                          <div className="text-amber-700 font-semibold">
                                            {t('insurance.claimGroup.outOfPocket', { amount: totalRef.complete && totalFranchise.complete ? moneyScope(resteACharge) : '—' })}
                                            <span className="font-normal text-gray-400 ml-1">
                                              {t('insurance.claimGroup.outOfPocketBreakdown', {
                                                deductible: totalFranchise.label,
                                                vat: totalRef.complete ? moneyScope(totalTva) : '—'
                                              })}
                                            </span>
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-500">{totalRef.label}</span>
                                      );
                                    })()}
                                  </button>

                                  {/* Status claims list */}
                                  {isStatusExpanded && (
                                    <div className="p-3 space-y-3 bg-white">
                                      {statusClaims.map(claim => (
                                        <ClaimCard
                                          key={claim.id}
                                          claim={claim}
                                          onView={setSelectedClaim}
                                          onDelete={handleDeleteClaim}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Vehicle Add Tool (used by KPI uninsured modal flow) */}
      {showAddTool && selectedContractForTool && (
        <VehicleAddTool
          contract={selectedContractForTool}
          onClose={() => setShowAddTool(false)}
          onSuccess={() => {
            setShowAddTool(false);
            loadData();
          }}
          onMessage={showMessage}
        />
      )}

      {/* KPI Modals */}
      {activeKpiModal === 'active-contracts' && (
        <ActiveContractsModal
          contracts={kpis.activeContractsList}
          vehicleInsurances={vehicleInsurances}
          intermediaries={intermediaries}
          onClose={() => setActiveKpiModal(null)}
          onViewContract={handleViewContractFromKpi}
        />
      )}

      {activeKpiModal === 'expiring' && (
        <ExpiringContractsModal
          contracts={kpis.expiringContractsList}
          vehicleInsurances={vehicleInsurances}
          intermediaries={intermediaries}
          onClose={() => setActiveKpiModal(null)}
          onViewContract={handleViewContractFromKpi}
        />
      )}

      {activeKpiModal === 'insured' && (
        <InsuredVehiclesModal
          vehicleInsurances={vehicleInsurances}
          vehicles={vehicles}
          contracts={contracts}
          coverageModels={coverageModels}
          insuredVehicleIds={kpis.insuredVehicleIds}
          onClose={() => setActiveKpiModal(null)}
        />
      )}

      {activeKpiModal === 'uninsured' && (
        <UninsuredVehiclesModal
          uninsuredVehicles={kpis.uninsuredVehiclesList}
          activeContracts={kpis.activeContractsList}
          onClose={() => setActiveKpiModal(null)}
          onLaunchAddTool={handleLaunchAddToolFromKpi}
        />
      )}

      {activeKpiModal === 'premium' && (
        <PremiumDetailModal
          vehicleInsurances={vehicleInsurances}
          vehicles={vehicles}
          contracts={contracts}
          coverageModels={coverageModels}
          insuredVehicleCount={kpis.insuredVehicles}
          onClose={() => setActiveKpiModal(null)}
        />
      )}

      {/* Vehicle Insurance Detail Modal */}
      {selectedVehicleForDetail && (
        <VehicleInsuranceDetailModal
          vehicle={selectedVehicleForDetail}
          vehicleInsurances={vehicleInsurances}
          contracts={contracts}
          coverageModels={coverageModels}
          onClose={() => setSelectedVehicleForDetail(null)}
        />
      )}

      {/* Contract Detail Modal */}
      {selectedContract && (
        <ContractDetailModal
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
          onUpdate={loadData}
          onWorkflowAction={handleContractWorkflowAction}
        />
      )}
    </div>
  );
}

export default InsuranceUserView;
